import { isPlatformBrowser } from '@angular/common';
import { computed, effect, inject, Injectable, PLATFORM_ID, signal, untracked } from '@angular/core';
import { PrinterConnectionStatusEnum, ProblemSeverityEnum, ProblemSourceEnum } from '../enums';
import { ApplicationProblemModel, CreateApplicationProblemParams } from '../models';
import { ApiHealthService } from './api-health.service';
import { OfflineService } from './offline.service';
import { PrinterConnectionService } from './printer-connection.service';
import { PrinterManagementService } from './printer-management.service';

/**
 * Centralized service for tracking application-wide problems.
 * Observes various system services and aggregates their issues into a single,
 * unified problem list for display in the UI.
 *
 * Problems automatically appear when detected and disappear when resolved.
 */
@Injectable({ providedIn: 'root' })
export class ApplicationProblemsService {
  readonly #platformId = inject(PLATFORM_ID);
  readonly #apiHealthService = inject(ApiHealthService);
  readonly #offlineService = inject(OfflineService);
  readonly #printerConnectionService = inject(PrinterConnectionService);
  readonly #printerManagementService = inject(PrinterManagementService);

  // Internal state - map of problem ID to problem
  readonly #problems = signal<Map<string, ApplicationProblemModel>>(new Map());

  // Exposed read-only signals
  /** All current problems as an array, sorted by severity (critical first) */
  readonly problems = computed(() => {
    const problemsMap = this.#problems();
    const problemsArray = Array.from(problemsMap.values());

    // Sort by severity: critical > warning > info
    const severityOrder: Record<ProblemSeverityEnum, number> = {
      [ProblemSeverityEnum.CRITICAL]: 0,
      [ProblemSeverityEnum.WARNING]: 1,
      [ProblemSeverityEnum.INFO]: 2,
    };

    return problemsArray.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);
  });

  /** Total number of problems */
  readonly count = computed(() => this.#problems().size);

  /** Whether there are any problems */
  readonly hasProblems = computed(() => this.#problems().size > 0);

  /** Number of critical problems */
  readonly criticalCount = computed(() => this.problems().filter((p) => p.severity === ProblemSeverityEnum.CRITICAL).length);

  /** Whether there are any critical problems */
  readonly hasCriticalProblems = computed(() => this.criticalCount() > 0);

  constructor() {
    if (!isPlatformBrowser(this.#platformId)) {
      return;
    }

    // Set up watchers for various system services
    this.watchApiHealth();
    this.watchNetworkStatus();
    this.watchSyncStatus();
    this.watchPrinterStatus();
  }

  /**
   * Add or update a problem
   */
  addProblem(params: CreateApplicationProblemParams): void {
    const id = params.subId ? `${params.source}:${params.subId}` : params.source;

    const problem: ApplicationProblemModel = {
      id,
      source: params.source,
      severity: params.severity,
      titleKey: params.titleKey,
      descriptionKey: params.descriptionKey,
      icon: params.icon,
      timestamp: new Date(),
      translationParams: params.translationParams,
    };

    this.#problems.update((map) => {
      const newMap = new Map(map);
      newMap.set(id, problem);
      return newMap;
    });
  }

  /**
   * Remove a problem by its ID or source
   */
  removeProblem(idOrSource: string): void {
    this.#problems.update((map) => {
      const newMap = new Map(map);
      // Try exact ID first
      if (newMap.has(idOrSource)) {
        newMap.delete(idOrSource);
      } else {
        // Remove all problems from this source
        for (const [key] of newMap) {
          if (key === idOrSource || key.startsWith(`${idOrSource}:`)) {
            newMap.delete(key);
          }
        }
      }
      return newMap;
    });
  }

  /**
   * Remove all problems from a specific source
   */
  removeProblemsFromSource(source: ProblemSourceEnum): void {
    this.#problems.update((map) => {
      const newMap = new Map(map);
      for (const [key, problem] of newMap) {
        if (problem.source === source) {
          newMap.delete(key);
        }
      }
      return newMap;
    });
  }

  /**
   * Clear all problems
   */
  clearAll(): void {
    this.#problems.set(new Map());
  }

  /**
   * Watch API health service and report problems
   */
  private watchApiHealth(): void {
    effect(() => {
      const isAvailable = this.#apiHealthService.isAvailable();
      const healthData = this.#apiHealthService.healthData();

      untracked(() => {
        if (!isAvailable) {
          // API is down - critical problem
          this.addProblem({
            source: ProblemSourceEnum.API,
            severity: ProblemSeverityEnum.CRITICAL,
            titleKey: 'problems.api.unavailable.title',
            descriptionKey: 'problems.api.unavailable.description',
            icon: 'api',
          });
        } else if (healthData?.overall === 'degraded') {
          // API is degraded - warning
          this.addProblem({
            source: ProblemSourceEnum.API,
            severity: ProblemSeverityEnum.WARNING,
            titleKey: 'problems.api.degraded.title',
            descriptionKey: 'problems.api.degraded.description',
            icon: 'api',
          });
        } else {
          // API is healthy - remove any API problems
          this.removeProblem(ProblemSourceEnum.API);
        }
      });
    });
  }

  /**
   * Watch network/offline status
   */
  private watchNetworkStatus(): void {
    effect(() => {
      const isOnline = this.#offlineService.isOnline();

      untracked(() => {
        if (!isOnline) {
          this.addProblem({
            source: ProblemSourceEnum.NETWORK,
            severity: ProblemSeverityEnum.CRITICAL,
            titleKey: 'problems.network.offline.title',
            descriptionKey: 'problems.network.offline.description',
            icon: 'disconnect',
          });
        } else {
          this.removeProblem(ProblemSourceEnum.NETWORK);
        }
      });
    });
  }

  /**
   * Watch sync status (syncing, pending operations)
   */
  private watchSyncStatus(): void {
    effect(() => {
      const syncInProgress = this.#offlineService.syncInProgress();
      const pendingCount = this.#offlineService.pendingOperationsCount();

      untracked(() => {
        if (syncInProgress) {
          // Syncing in progress - info level
          this.addProblem({
            source: ProblemSourceEnum.SYNC,
            severity: ProblemSeverityEnum.INFO,
            titleKey: 'problems.sync.syncing.title',
            descriptionKey: 'problems.sync.syncing.description',
            icon: 'sync',
          });
        } else if (pendingCount > 0) {
          // Has pending operations waiting to sync - warning
          this.addProblem({
            source: ProblemSourceEnum.SYNC,
            severity: ProblemSeverityEnum.WARNING,
            titleKey: 'problems.sync.pending.title',
            descriptionKey: 'problems.sync.pending.description',
            icon: 'cloud-upload',
            translationParams: { count: pendingCount },
          });
        } else {
          // All synced - remove sync problems
          this.removeProblem(ProblemSourceEnum.SYNC);
        }
      });
    });
  }

  /**
   * Watch printer connection status
   */
  private watchPrinterStatus(): void {
    effect(() => {
      const isPrintingEnabled = this.#printerManagementService.isEnabled();
      const overallStatus = this.#printerConnectionService.overallStatus();
      const errorCount = this.#printerConnectionService.errorCount();
      const totalCount = this.#printerConnectionService.totalCount();

      untracked(() => {
        // Only track printer problems if printing is enabled and we have printers configured
        if (!isPrintingEnabled || totalCount === 0) {
          this.removeProblem(ProblemSourceEnum.PRINTER);
          return;
        }

        switch (overallStatus) {
          case PrinterConnectionStatusEnum.ERROR:
            this.addProblem({
              source: ProblemSourceEnum.PRINTER,
              severity: ProblemSeverityEnum.WARNING,
              titleKey: 'problems.printer.error.title',
              descriptionKey: 'problems.printer.error.description',
              icon: 'printer',
              translationParams: { count: errorCount },
            });
            break;

          case PrinterConnectionStatusEnum.DISCONNECTED:
            this.addProblem({
              source: ProblemSourceEnum.PRINTER,
              severity: ProblemSeverityEnum.WARNING,
              titleKey: 'problems.printer.disconnected.title',
              descriptionKey: 'problems.printer.disconnected.description',
              icon: 'printer',
            });
            break;

          case PrinterConnectionStatusEnum.PARTIAL:
            this.addProblem({
              source: ProblemSourceEnum.PRINTER,
              severity: ProblemSeverityEnum.INFO,
              titleKey: 'problems.printer.partial.title',
              descriptionKey: 'problems.printer.partial.description',
              icon: 'printer',
            });
            break;

          case PrinterConnectionStatusEnum.HEALTHY:
          case PrinterConnectionStatusEnum.UNKNOWN:
          default:
            this.removeProblem(ProblemSourceEnum.PRINTER);
            break;
        }
      });
    });
  }

  /**
   * Manually report a FiscalNet problem (called from FiscalService on error)
   */
  reportFiscalNetError(errorMessage?: string): void {
    this.addProblem({
      source: ProblemSourceEnum.FISCALNET,
      severity: ProblemSeverityEnum.CRITICAL,
      titleKey: 'problems.fiscalnet.error.title',
      descriptionKey: 'problems.fiscalnet.error.description',
      icon: 'file-protect',
      translationParams: errorMessage ? { error: errorMessage } : undefined,
    });
  }

  /**
   * Clear FiscalNet problem (called after successful FiscalNet operation)
   */
  clearFiscalNetError(): void {
    this.removeProblem(ProblemSourceEnum.FISCALNET);
  }

  /**
   * Manually report a WebSocket connection problem
   */
  reportWebSocketError(namespace: string): void {
    this.addProblem({
      source: ProblemSourceEnum.WEBSOCKET,
      subId: namespace,
      severity: ProblemSeverityEnum.WARNING,
      titleKey: 'problems.websocket.disconnected.title',
      descriptionKey: 'problems.websocket.disconnected.description',
      icon: 'cloud-server',
      translationParams: { namespace },
    });
  }

  /**
   * Clear WebSocket problem for a specific namespace
   */
  clearWebSocketError(namespace: string): void {
    this.removeProblem(`${ProblemSourceEnum.WEBSOCKET}:${namespace}`);
  }

  /**
   * Report session expiring soon
   */
  reportSessionExpiring(minutesRemaining: number): void {
    this.addProblem({
      source: ProblemSourceEnum.SESSION,
      severity: minutesRemaining <= 1 ? ProblemSeverityEnum.CRITICAL : ProblemSeverityEnum.WARNING,
      titleKey: 'problems.session.expiring.title',
      descriptionKey: 'problems.session.expiring.description',
      icon: 'clock-circle',
      translationParams: { minutes: minutesRemaining },
    });
  }

  /**
   * Clear session expiring warning
   */
  clearSessionExpiring(): void {
    this.removeProblem(ProblemSourceEnum.SESSION);
  }
}
